// Session-scoped trip context for the HIRE flow.
//
// Mirrors the cab flow's sramika_ss_home_* sessionStorage keys, under a separate
// sramika_ss_hire_* namespace. The two flows must not share these: a customer
// searching for a cab has not thereby chosen a route for hiring a driver, and
// letting one prefill the other silently attributes intent they never expressed.
//
// Durable prefill across visits stays in localStorage under
// sramika_trip_history_HIRE_${phone} — see HireLocationPicker. This module is only
// the current trip, so that a refresh mid-flow does not lose it.
const KEYS = {
  fromLocation: "sramika_ss_hire_fromLocation",
  toLocation: "sramika_ss_hire_toLocation",
  // The canonical city the picker resolved from the chosen location — the value
  // drivers are actually registered against. Stored separately from the locations
  // because it cannot be recovered from them: "Gollapudi, Vijayawada, …" splits to
  // the locality, and searching that finds nobody.
  selectedCity: "sramika_ss_hire_selectedCity",
  // The schedule. A hire is priced on how long the driver is needed, so these are
  // as much a part of "can I price this" as the route is.
  tripType: "sramika_ss_hire_tripType",
  pickupDateTime: "sramika_ss_hire_pickupDateTime",
  dropDateTime: "sramika_ss_hire_dropDateTime",
};

export function saveHireTripContext({
  fromLocation, toLocation, selectedCity, tripType, pickupDateTime, dropDateTime,
}) {
  const write = (key, value) => {
    if (value === undefined) return;
    sessionStorage.setItem(key, JSON.stringify(value));
  };
  try {
    write(KEYS.fromLocation, fromLocation);
    write(KEYS.toLocation, toLocation);
    write(KEYS.selectedCity, selectedCity);
    write(KEYS.tripType, tripType);
    write(KEYS.pickupDateTime, pickupDateTime);
    write(KEYS.dropDateTime, dropDateTime);
  } catch { /* storage unavailable - context simply does not survive a refresh */ }
}

export function loadHireTripContext() {
  const read = (key) => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  };
  return {
    fromLocation: read(KEYS.fromLocation),
    toLocation: read(KEYS.toLocation),
    selectedCity: read(KEYS.selectedCity),
    tripType: read(KEYS.tripType),
    pickupDateTime: read(KEYS.pickupDateTime),
    dropDateTime: read(KEYS.dropDateTime),
  };
}

// A location may be a plain string or a { value } object depending on where it
// came from. Callers that only need text should go through this.
export function locationText(loc) {
  if (!loc) return "";
  if (typeof loc === "string") return loc;
  return loc.value || loc.label || loc.description || "";
}

// Whether we know enough to price a hire. Both ends are required: a fare needs an
// origin AND a destination, and half a route prices nothing.
//
// This is the single gate for every hire surface that shows money — the profile's
// rates, the modal, and the card. The cab flow's equivalent is CarDetail's
// `hasRoute`, which additionally clears itself when a fare fetch fails; when hire
// pricing lands, a failed calculation should fail closed here in the same way
// rather than falling back to a stored rate.
export function hasHireTripContext(fromLocation, toLocation) {
  return !!(locationText(fromLocation) && locationText(toLocation));
}

// Reads the stored context and answers the same question — for callers that hold
// no resolved location of their own.
export function hasStoredHireTripContext() {
  const { fromLocation, toLocation } = loadHireTripContext();
  return hasHireTripContext(fromLocation, toLocation);
}
